import React, { useState } from "react";
import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Toolbar,
  Tooltip,
  Divider,
  IconButton,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { getMenuItemsForRoleAndPermissions, MenuItem } from "./menuConfig";
import Logo from "../assets/images/final-logo.png";

export const DRAWER_WIDTH = 260;
export const MINI_DRAWER_WIDTH = 72;

const TRANSITION = "width 225ms cubic-bezier(0.4, 0, 0.2, 1)";

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export function Sidebar({ mobileOpen, onMobileClose, collapsed, onToggleCollapse }: SidebarProps) {
  const location = useLocation();
  const { user, permissions } = useAuth();
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const role = user?.role || "";
  const filteredItems = getMenuItemsForRoleAndPermissions(role, permissions);
  const effectiveWidth = collapsed ? MINI_DRAWER_WIDTH : DRAWER_WIDTH;

  const toggleSubmenu = (text: string) => {
    setOpenMenus((prev) => ({ ...prev, [text]: !prev[text] }));
  };

  const isActive = (path: string) => location.pathname === path;

  const renderMenuItem = (item: MenuItem) => {
    const hasSubItems = item.subItems && item.subItems.length > 0;

    if (hasSubItems) {
      const isOpen = openMenus[item.text] || false;
      const anyChildActive = item.subItems!.some((sub) => isActive(sub.path));

      const handleParentClick = () => {
        if (collapsed) {
          onToggleCollapse();
          setOpenMenus((prev) => ({ ...prev, [item.text]: true }));
        } else {
          toggleSubmenu(item.text);
        }
      };

      const parentButton = (
        <ListItemButton
          onClick={handleParentClick}
          sx={{
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
            justifyContent: collapsed ? "center" : "initial",
            px: collapsed ? 1.5 : 2,
            bgcolor: anyChildActive ? "action.selected" : "transparent",
          }}
        >
          <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, justifyContent: "center" }}>
            <i className={item.iconClass} style={{ fontSize: 16, color: "#666" }} />
          </ListItemIcon>
          {!collapsed && (
            <>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
              />
              {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </>
          )}
        </ListItemButton>
      );

      return (
        <React.Fragment key={item.text}>
          {collapsed ? (
            <Tooltip title={item.text} placement="right" arrow>
              {parentButton}
            </Tooltip>
          ) : (
            parentButton
          )}
          {!collapsed && (
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List disablePadding>
                {item.subItems!.map((subItem) => (
                  <ListItemButton
                    key={subItem.path}
                    component={NavLink}
                    to={subItem.path}
                    onClick={onMobileClose}
                    sx={{
                      pl: 5,
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.25,
                      bgcolor: isActive(subItem.path) ? "brand.main" : "transparent",
                      color: isActive(subItem.path) ? "#fff" : "text.primary",
                      "&:hover": {
                        bgcolor: isActive(subItem.path) ? "brand.dark" : "action.hover",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <i
                        className={subItem.iconClass}
                        style={{
                          fontSize: 14,
                          color: isActive(subItem.path) ? "#fff" : "#888",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={subItem.text}
                      primaryTypographyProps={{ fontSize: 13 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    }

    const button = (
      <ListItemButton
        key={item.path}
        component={NavLink}
        to={item.path}
        onClick={onMobileClose}
        sx={{
          borderRadius: 1,
          mx: 1,
          mb: 0.5,
          justifyContent: collapsed ? "center" : "initial",
          px: collapsed ? 1.5 : 2,
          bgcolor: isActive(item.path) ? "brand.main" : "transparent",
          color: isActive(item.path) ? "#fff" : "text.primary",
          "&:hover": {
            bgcolor: isActive(item.path) ? "brand.dark" : "action.hover",
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, justifyContent: "center" }}>
          <i
            className={item.iconClass}
            style={{
              fontSize: 16,
              color: isActive(item.path) ? "#fff" : "#666",
            }}
          />
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
          />
        )}
      </ListItemButton>
    );

    return collapsed ? (
      <Tooltip title={item.text} placement="right" arrow key={item.path}>
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar sx={{ justifyContent: "center", py: 2, minHeight: "auto !important" }}>
        <img
          src={Logo}
          alt="Kranzler Eck"
          style={{
            maxWidth: collapsed ? 40 : 160,
            height: "auto",
            transition: "max-width 225ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      </Toolbar>
      <Box sx={{ overflow: "auto", flex: 1 }}>
        <List>{filteredItems.map(renderMenuItem)}</List>
      </Box>
      <Divider />
      <Box sx={{ display: { xs: "none", md: "flex" }, justifyContent: collapsed ? "center" : "flex-end", p: 1 }}>
        <IconButton onClick={onToggleCollapse} size="small">
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>
    </Box>
  );

  // Mobile drawer always uses full width, no collapse
  const mobileDrawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar sx={{ justifyContent: "center", py: 2 }}>
        <img src={Logo} alt="Kranzler Eck" style={{ maxWidth: 160, height: "auto" }} />
      </Toolbar>
      <Box sx={{ overflow: "auto", flex: 1 }}>
        <List>
          {filteredItems.map((item) => {
            const hasSubItems = item.subItems && item.subItems.length > 0;
            if (hasSubItems) {
              const isOpen = openMenus[item.text] || false;
              const anyChildActive = item.subItems!.some((sub) => isActive(sub.path));
              return (
                <React.Fragment key={item.text}>
                  <ListItemButton
                    onClick={() => toggleSubmenu(item.text)}
                    sx={{ borderRadius: 1, mx: 1, mb: 0.5, bgcolor: anyChildActive ? "action.selected" : "transparent" }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <i className={item.iconClass} style={{ fontSize: 16, color: "#666" }} />
                    </ListItemIcon>
                    <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
                    {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </ListItemButton>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      {item.subItems!.map((subItem) => (
                        <ListItemButton
                          key={subItem.path}
                          component={NavLink}
                          to={subItem.path}
                          onClick={onMobileClose}
                          sx={{
                            pl: 5, borderRadius: 1, mx: 1, mb: 0.25,
                            bgcolor: isActive(subItem.path) ? "brand.main" : "transparent",
                            color: isActive(subItem.path) ? "#fff" : "text.primary",
                            "&:hover": { bgcolor: isActive(subItem.path) ? "brand.dark" : "action.hover" },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <i className={subItem.iconClass} style={{ fontSize: 14, color: isActive(subItem.path) ? "#fff" : "#888" }} />
                          </ListItemIcon>
                          <ListItemText primary={subItem.text} primaryTypographyProps={{ fontSize: 13 }} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }
            return (
              <ListItemButton
                key={item.path}
                component={NavLink}
                to={item.path}
                onClick={onMobileClose}
                sx={{
                  borderRadius: 1, mx: 1, mb: 0.5,
                  bgcolor: isActive(item.path) ? "brand.main" : "transparent",
                  color: isActive(item.path) ? "#fff" : "text.primary",
                  "&:hover": { bgcolor: isActive(item.path) ? "brand.dark" : "action.hover" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <i className={item.iconClass} style={{ fontSize: 16, color: isActive(item.path) ? "#fff" : "#666" }} />
                </ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }} />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { md: effectiveWidth },
        flexShrink: { md: 0 },
        transition: TRANSITION,
      }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: DRAWER_WIDTH },
        }}
      >
        {mobileDrawerContent}
      </Drawer>
      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: effectiveWidth,
            transition: TRANSITION,
            borderRight: "1px solid",
            borderColor: "divider",
            overflowX: "hidden",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
}
